import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useBillingSalesQuery } from '@/queries/billing'
import type { BillingSale } from '@/types/billing'
import { formatMinor } from './money'

export function SalesSection({ accountId }: { accountId: number }) {
  const { t, i18n } = useTranslation()
  const { data = [], isLoading, isError } = useBillingSalesQuery(accountId)
  // Written out so the dead-key gate can see every label is used.
  const statusLabel: Record<BillingSale['status'], string> = {
    pending: t('billing.sales.status_pending'),
    paid: t('billing.sales.status_paid'),
    refunded: t('billing.sales.status_refunded'),
    partially_refunded: t('billing.sales.status_partially_refunded'),
  }

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">{t('billing.sales.title')}</h2>
      {isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : isError ? (
        <p className="text-sm text-destructive">{t('billing.errors.load')}</p>
      ) : data.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('billing.sales.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('billing.sales.payer')}</TableHead>
                <TableHead>{t('billing.sales.pack')}</TableHead>
                <TableHead>{t('billing.sales.amount')}</TableHead>
                <TableHead>{t('billing.sales.fee')}</TableHead>
                <TableHead>{t('billing.sales.status')}</TableHead>
                <TableHead>{t('billing.sales.date')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((sale) => (
                <TableRow key={sale.sale_id}>
                  <TableCell className="font-mono text-xs">{sale.payer}</TableCell>
                  <TableCell>{sale.pack}</TableCell>
                  <TableCell>{formatMinor(sale.amount_units, sale.currency, i18n.language)}</TableCell>
                  <TableCell>{formatMinor(sale.fee_units, sale.currency, i18n.language)}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{statusLabel[sale.status]}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {sale.paid_at ? new Date(sale.paid_at).toLocaleDateString(i18n.language) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  )
}
